import {
  memo,
  useEffect,
  useState,
  type ComponentType,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { GlobalFooter } from '@fff-web/fff-global-footer';
import { GlobalNav, NewMemberNavTemplate } from '@fff-web/fff-global-nav';
import { FFFErrorBoundary } from '@fff-web/fff-ui-shared';
import { useCampaigns, useUserContext } from '@fff-web/fff-utilities';
import { redirectToLogin } from '../../utils/redirectMethods';
import { Loading } from '../Loading';

type FFFGlobalNavProps = {
  useMemorized: boolean;
};

const FFFGlobalNav: FunctionComponent<FFFGlobalNavProps> = ({ useMemorized }) => {
  return useMemorized ? (
    <MemoizedGlobalNav />
  ) : (
    <GlobalNav env={process.env.REACT_APP_ENVIRONMENT} />
  );
};

const MemoizedGlobalNav = memo(function MemoizedGlobalNavComponent() {
  return <GlobalNav env={process.env.REACT_APP_ENVIRONMENT} />;
});

type LayoutProps = {
  pageTitle?: string;
  isLoading?: boolean;
  redirectIfNoAuth?: boolean;
  isNMF?: boolean;
  useMemorized?: boolean;
  meta?: {
    links?: Array<{
      href: string;
      [key: string]: unknown;
    }>;
    scripts?: Array<{
      src: string;
      [key: string]: unknown;
    }>;
    metadata?: Array<{
      name: string;
      content: string;
      [key: string]: unknown;
    }>;
  };
  showError?: boolean;
  ErrorComponent?: ComponentType;
  hideChatWidget?: boolean;
};

const Layout: FunctionComponent<PropsWithChildren<LayoutProps>> = ({
  children,
  pageTitle = 'FabFitFun',
  meta = {},
  isLoading = false,
  redirectIfNoAuth = false,
  isNMF = false,
  useMemorized = true,
  showError = false,
  ErrorComponent = () => <></>,
  hideChatWidget,
}) => {
  const [hasPermissions, setHasPermissions] = useState(!redirectIfNoAuth);
  const { userInfo } = useUserContext();
  const { pathname } = useLocation();
  const loggedIn = userInfo && userInfo.loggedIn;
  const { editRefillAccess, editUserAccess } = useCampaigns();
  const isEditCampaignOpen = editRefillAccess && editUserAccess;

  const renderLayout = () => (
    <div data-testid="layout-container">
      <FFFErrorBoundary fallbackUI={<ErrorComponent />} forceError={showError}>
        {children}
      </FFFErrorBoundary>
    </div>
  );

  useEffect(() => {
    // Redirect to login if redirectIfNoAuth prop is true and user is logged out
    if (redirectIfNoAuth) {
      if (!loggedIn) {
        redirectToLogin();
      } else {
        setHasPermissions(true);
      }
    }
  }, [redirectIfNoAuth]);

  return (
    hasPermissions && (
      <>
        <Helmet>
          <title data-testid="layout">{pageTitle}</title>
          {meta &&
            meta.links &&
            meta.links.map(({ href, ...link }) => (
              <link key={href} {...link} href={href} rel="stylesheet" type="text/css" />
            ))}
          {meta &&
            meta.scripts &&
            meta.scripts.map(({ src, ...script }) => <script key={src} {...script} src={src} />)}
          {meta && meta.metadata ? (
            meta.metadata.map(({ name, content, ...rest }) => (
              <meta key={name} name={name} property={`og:${name}`} content={content} {...rest} />
            ))
          ) : (
            <meta name="description" content="My Account apps" />
          )}
        </Helmet>
        {isNMF ? (
          <NewMemberNavTemplate
            env={process.env.REACT_APP_ENVIRONMENT}
            step="shipping"
            isEditCampaignOpen={isEditCampaignOpen}
          />
        ) : (
          <FFFGlobalNav useMemorized={useMemorized} />
        )}
        {isLoading ? <Loading /> : renderLayout()}
        <GlobalFooter
          pathname={pathname}
          chatbotConfig={{
            agentId: process.env.REACT_APP_APPLIED_LABS_AGENT_ID,
            hideWidget: hideChatWidget,
          }}
        />
      </>
    )
  );
};

export default Layout;
